import { useEffect, useRef, useState } from "react";
import { useT } from "../i18n";
import { useSitzung } from "../schale/Sitzung";
import { useRuf } from "../schale/Ruf";
import { ATTRAPPE, Fehler, sitzungsendeGrund } from "../daten/klient";
import * as R from "../daten/routen";
import { faktorBeiAnmeldungEinrichten, type Faktoreinrichtung } from "../daten/faktor";
import { useWarteschlange } from "../daten/warteschlange";
import type { Anmeldung as Angemeldet } from "../daten/typen";
import { leeresFeld } from "./anmeldefeld";
import "../stile/offline.css";

/* ══════════════════════════════════════════════════════════════════
   Der Ablauf der Anmeldung — einmal, für zwei Oberflächen (19.09.2026).

   Bis hierher stand er in `Anmeldung.tsx`, mitten im Formular des
   Schreibtischs. Seit die Anmeldung am Telefon eine eigene Maske hat
   (`AnmeldungTelefon.tsx`), gäbe es ihn sonst zweimal — und zwei
   Abläufe laufen auseinander: eine Stufe, die der eine kennt und der
   andere nicht, ein Fehler, den nur einer richtig übersetzt.

   **Hier steht, was geschieht; wie es aussieht, steht in den Masken.**
   Der Inhalt ist unverändert aus `Anmeldung.tsx` übernommen — der
   zweite Faktor wird erfragt und nicht vorausgesetzt, der fällige
   Passwortwechsel legt sich über die Anmeldung, und im Prototyp ist der
   Code 123456. Neu ist nur `codeSetzen`: Die Telefonmaske hat ein Feld
   für alle sechs Ziffern statt sechs Kästen.
   ══════════════════════════════════════════════════════════════════ */

/** Die Untergrenze der API (`POST /anmeldung/passwort`) — hier nur, um früher zu sagen, was fehlt. */
export const MINDESTLAENGE = 12;

/**
 * Der Satz für den, der im Funkloch neu startet.
 *
 * Ohne Netz gibt es keine Anmeldung: Das Kontext-Token lebt im Speicher und
 * wird über `POST /api/kontext` erneuert — beides braucht den Server. Wichtiger
 * als die Erklärung ist die zweite Zeile: **Was aufgenommen wurde, liegt auf
 * dem Gerät.** Wer das nicht liest, hält seine Übergabe für verloren und nimmt
 * sie ein zweites Mal auf.
 */
export function Anmeldesperre() {
  const t = useT();
  const wartende = useWarteschlange();
  const [ohneNetz, setzeOhneNetz] = useState(() => navigator.onLine === false);
  useEffect(() => {
    const hin = () => setzeOhneNetz(false);
    const weg = () => setzeOhneNetz(true);
    window.addEventListener("online", hin);
    window.addEventListener("offline", weg);
    return () => {
      window.removeEventListener("online", hin);
      window.removeEventListener("offline", weg);
    };
  }, []);
  if (!ohneNetz) return null;
  return (
    <p className="netzband" role="status">
      <b>{t("offline.anmeldungTitel")}</b>
      <span>
        {wartende.length
          ? t(wartende.length === 1 ? "offline.anmeldungWartetEiner" : "offline.anmeldungWartet", { zahl: wartende.length })
          : t("offline.anmeldungSatz")}
      </span>
    </p>
  );
}

/** Die leeren sechs Ziffern. */
const LEER = ["", "", "", "", "", ""];

/**
 * Die Anmeldung als Zustand und Handgriffe — E-Mail und Passwort, der
 * zweite Faktor, der fällige Wechsel.
 */
export function useAnmeldelauf() {
  const t = useT();
  const { anmelden } = useSitzung();
  const ruf = useRuf();

  const [stufe, setzeStufe] = useState<"anmelden" | "code">("anmelden");
  const [mail, setzeMail] = useState(ATTRAPPE ? "lisa@mr-machine.de" : "");
  const [wort, setzeWort] = useState(ATTRAPPE ? "ganzegal" : "");
  const [merken, setzeMerken] = useState(true);
  const [fehler, setzeFehler] = useState<string | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);
  /* **Warum man hier steht** (A-09): Hat die Verwaltung einen Passwortwechsel
     „sofort" verlangt, endet die Sitzung binnen zwei Minuten — bis dahin
     ohne ein Wort, und wer mitten in der Planung war, hielt es für einen
     Absturz. */
  const [hinweis] = useState<string | null>(() =>
    sitzungsendeGrund() === "passwortwechsel" ? t("anmeldung.rauswurfWechsel") : null);
  /** Das Feld, das vor dem Absenden leer war — es trägt dann `aria-invalid`. */
  const [leer, setzeLeer] = useState<"mail" | "wort" | null>(null);
  /* **Die Sperre wartet nicht auf das Neuzeichnen** (A-02). `disabled={laeuft}`
     greift erst nach dem nächsten Zeichnen — zwei Tipps im selben Takt
     schickten zwei Anmeldungen ab. Der Riegel steht im Handler. */
  const unterwegs = useRef(false);
  // Die Verwaltung verlangt ein neues Passwort: Das Fenster liegt über der Anmeldung.
  const [wechsel, setzeWechsel] = useState(false);

  /* ---------- Der Faktor, der erst eingerichtet wird (E4, A-06) ----------
     Verlangt die Verwaltung einen zweiten Faktor und ist noch keiner da,
     antwortet die Anmeldung mit dem Code `403 anmeldung.faktor_fehlt`. Dann
     richtet dieser Schritt die App gleich ein: Bild und Schlüssel erscheinen
     über den Ziffern, und der erste Code bestätigt den Faktor und meldet an. */
  const [einrichtung, setzeEinrichtung] = useState<Faktoreinrichtung | null>(null);

  /* ---------- Die sechs Ziffern ---------- */
  const [ziffern, setzeZiffern] = useState<string[]>(LEER);
  const [falsch, setzeFalsch] = useState(false);
  /** Die Felder des Codes. Am Telefon ist es eines — es steht dann an Stelle 0. */
  const felder = useRef<(HTMLInputElement | null)[]>([]);

  /* Der Code-Schritt ist der eine, in dem sofort getippt wird: Er kommt nur
     nach „Anmelden", und die Tastatur des Telefons bietet den Code aus der
     Passwortverwaltung erst an, wenn das Feld den Fokus hat. */
  useEffect(() => {
    if (stufe === "code") felder.current[0]?.focus();
  }, [stufe]);

  /**
   * Der zweite Faktor wird nicht vorausgesetzt, sondern **erfragt**.
   *
   * Im Prototyp ging es von hier immer zum Code — dort gab es keine API, die
   * hätte widersprechen können. Gegen `apps/api` ist das falsch: Ob ein Konto
   * einen zweiten Faktor braucht, steht in `staff.mfa_required`, und der
   * Erstbenutzer hat ihn nicht (35-anmeldung-steht.md). Wer trotzdem einen
   * Code verlangt, sperrt ihn aus.
   *
   * Also: anmelden. Kommt `anmeldung.faktor_noetig` zurück, dann — und nur
   * dann — der Code.
   */
  const anmeldenVersuchen = async () => {
    if (unterwegs.current) return;
    const fehlt = leeresFeld(mail, wort);
    setzeLeer(fehlt);
    if (fehlt) {
      setzeFehler(t(fehlt === "mail" ? "anmeldung.mailFehlt" : "anmeldung.passwortFehlt"));
      return;
    }
    unterwegs.current = true;
    setzeFehler(null);
    setzeLaeuft(true);
    try {
      const antwort = await R.anmelden(mail, wort);
      anmelden(antwort.ich);
      ruf("willkommen", { name: antwort.ich.name });
    } catch (e) {
      unterwegs.current = false;
      if (e instanceof Fehler && e.schluessel === "anmeldung.faktor_noetig") {
        setzeLaeuft(false);
        setzeStufe("code");
        return;
      }
      /* **Verlangt, aber noch keiner da** (N4-05): gleich einrichten. Bis
         dahin kam zuerst „Code eingeben" — aus einer App, die es noch nicht
         gab —, und erst ein beliebiger Code brachte das Bild. */
      if (e instanceof Fehler && e.schluessel === "anmeldung.faktor_fehlt") {
        try {
          setzeEinrichtung(await faktorBeiAnmeldungEinrichten(mail, wort));
          setzeZiffern(LEER);
          setzeFehler(t("faktor.erstEinrichten"));
          setzeStufe("code");
        } catch (f) {
          setzeFehler(f instanceof Fehler ? t(f.textPfad, f.werte) : t("fehler.unbekannt"));
        }
        setzeLaeuft(false);
        return;
      }
      // Das Passwort stimmt — aber hinein geht es erst mit einem neuen.
      if (e instanceof Fehler && e.schluessel === "anmeldung.passwortwechsel_faellig") {
        setzeLaeuft(false);
        setzeWechsel(true);
        return;
      }
      setzeFehler(e instanceof Fehler ? t(e.textPfad, e.werte) : t("fehler.unbekannt"));
      setzeLaeuft(false);
    }
  };

  const gewechselt = (antwort: Angemeldet) => {
    anmelden(antwort.ich);
    ruf("passwortGewechselt", { name: antwort.ich.name });
  };

  /* **Der Code kommt als Wert, nicht aus dem Zustand** (Durchgang
     03.10.2026, A-06). `codePruefen` läuft im selben Zug wie
     `setzeZiffern` — `ziffern` ist dann noch der Stand davor, und nach dem
     Einfügen von „123 456" ging „12345" hinaus. */
  const fertigMelden = async (code: string) => {
    if (unterwegs.current) return;
    unterwegs.current = true;
    setzeLaeuft(true);
    try {
      const antwort = await R.anmelden(mail, wort, code, einrichtung?.faktor_id);
      anmelden(antwort.ich);
      ruf(einrichtung ? "faktorEingerichtet" : "willkommen", { name: antwort.ich.name });
    } catch (e) {
      unterwegs.current = false;
      /* Kein Faktor da, aber verlangt: einrichten statt abweisen (E4). */
      if (e instanceof Fehler && e.schluessel === "anmeldung.faktor_fehlt" && !einrichtung) {
        try {
          setzeEinrichtung(await faktorBeiAnmeldungEinrichten(mail, wort));
          setzeZiffern(LEER);
          setzeFehler(t("faktor.erstEinrichten"));
        } catch (f) {
          setzeFehler(f instanceof Fehler ? t(f.textPfad, f.werte) : t("fehler.unbekannt"));
        }
        setzeLaeuft(false);
        felder.current[0]?.focus();
        return;
      }
      setzeFehler(e instanceof Fehler ? t(e.textPfad, e.werte) : t("fehler.unbekannt"));
      setzeLaeuft(false);
      // Ein falscher Code wird neu getippt, nicht nachgebessert.
      if (e instanceof Fehler && e.schluessel === "anmeldung.code_falsch") {
        setzeFalsch(true);
        window.setTimeout(() => {
          setzeZiffern(LEER);
          setzeFalsch(false);
          felder.current[0]?.focus();
        }, 600);
      }
    }
  };

  const codePruefen = (eingabe: string) => {
    if (eingabe.length < 6) {
      setzeFehler(t("anmeldung.codeZuKurz"));
      return;
    }
    // Ohne API steht der Code fest — genau wie im Prototyp, damit man
    // sieht, dass geprüft wird. Mit API prüft die API.
    if (ATTRAPPE && eingabe !== "123456") {
      setzeFalsch(true);
      setzeFehler(t("anmeldung.codeStimmtNicht"));
      window.setTimeout(() => {
        setzeZiffern(LEER);
        setzeFalsch(false);
        felder.current[0]?.focus();
      }, 600);
      return;
    }
    void fertigMelden(eingabe);
  };

  const zifferSetzen = (i: number, wert: string) => {
    const ziffernNur = wert.replace(/\D/g, "");
    /* **Die Tastatur des Telefons bietet den Code selbst an** (17.09.2026,
       `autoComplete="one-time-code"` am ersten Feld). Sie schreibt dann alle
       sechs Ziffern in dieses eine Feld — wer hier auf eine Ziffer kürzt,
       wirft fünf davon weg und lässt den Nutzer trotzdem tippen. Dasselbe
       gilt für das Einfügen aus der Zwischenablage, nur dass dafür schon
       `einfuegen` zuständig ist; ein Autofill löst kein `paste` aus. */
    if (ziffernNur.length > 1) {
      const verteilt = Array.from({ length: 6 }, (_, k) => ziffernNur[k - i] ?? ziffern[k] ?? "");
      setzeZiffern(verteilt);
      setzeFalsch(false);
      setzeFehler(null);
      if (verteilt.every((z) => z)) codePruefen(verteilt.join(""));
      else felder.current[Math.min(i + ziffernNur.length, 5)]?.focus();
      return;
    }
    const nur = ziffernNur.slice(0, 1);
    const neu = [...ziffern];
    neu[i] = nur;
    setzeZiffern(neu);
    setzeFalsch(false);
    setzeFehler(null);
    if (nur && i < 5) felder.current[i + 1]?.focus();
    if (neu.every((z) => z)) codePruefen(neu.join(""));
  };

  /**
   * **Ein Feld für alle sechs Ziffern** (Telefon, 19.09.2026). Getippt,
   * eingefügt oder von der Passwortverwaltung eingesetzt — es kommt immer
   * als ganze Zeichenkette an, und ab der sechsten Ziffer wird geprüft.
   */
  const codeSetzen = (wert: string) => {
    const nur = wert.replace(/\D/g, "").slice(0, 6);
    setzeZiffern(Array.from({ length: 6 }, (_, k) => nur[k] ?? ""));
    setzeFalsch(false);
    setzeFehler(null);
    if (nur.length === 6) codePruefen(nur);
  };

  const taste = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !ziffern[i] && i > 0) felder.current[i - 1]?.focus();
    if (e.key === "ArrowLeft" && i > 0) felder.current[i - 1]?.focus();
    if (e.key === "ArrowRight" && i < 5) felder.current[i + 1]?.focus();
  };

  const einfuegen = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!text) return;
    e.preventDefault();
    const neu = Array.from({ length: 6 }, (_, k) => text[k] ?? "");
    setzeZiffern(neu);
    if (text.length >= 6) codePruefen(neu.join(""));
  };

  const zurueckZurAnmeldung = () => {
    setzeFehler(null);
    setzeEinrichtung(null);
    setzeStufe("anmelden");
  };

  return {
    stufe, setzeStufe, zurueckZurAnmeldung,
    mail, setzeMail: (w: string) => { setzeMail(w); if (leer === "mail") setzeLeer(null); },
    wort, setzeWort: (w: string) => { setzeWort(w); if (leer === "wort") setzeLeer(null); },
    merken, setzeMerken, leer, hinweis,
    fehler, laeuft, wechsel, setzeWechsel, einrichtung,
    ziffern, falsch, felder,
    anmeldenVersuchen, gewechselt, codePruefen, zifferSetzen, codeSetzen, taste, einfuegen,
  };
}

/**
 * Der fällige Passwortwechsel (Migration 0101) als Zustand und Handgriff —
 * das Fenster am Schreibtisch und der Schritt am Telefon teilen ihn.
 */
export function usePasswortwechsel({ mail, altes, drin }: {
  mail: string;
  altes: string;
  drin: (antwort: Angemeldet) => void;
}) {
  const t = useT();
  const [neu, setzeNeu] = useState("");
  const [nochmal, setzeNochmal] = useState("");
  const [fehler, setzeFehler] = useState<string | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);

  const speichern = async () => {
    setzeFehler(null);
    // Was die Maske selbst sehen kann, sagt sie, bevor sie fragt.
    if (neu.length < MINDESTLAENGE) { setzeFehler(t("anmeldung.wechselZuKurz", { n: MINDESTLAENGE })); return; }
    if (neu !== nochmal) { setzeFehler(t("anmeldung.wechselUngleich")); return; }
    if (neu === altes) { setzeFehler(t("fehler.anmeldungPasswortGleich")); return; }

    setzeLaeuft(true);
    try {
      drin(await R.passwortWechseln(mail, altes, neu));
    } catch (e) {
      setzeFehler(e instanceof Fehler ? t(e.textPfad, e.werte) : t("fehler.unbekannt"));
      setzeLaeuft(false);
    }
  };

  return { neu, setzeNeu, nochmal, setzeNochmal, fehler, laeuft, speichern };
}
